/**
 * ZenoWell C9 页面交互。
 * 能力标签、FAQ 与预约弹窗均使用原生 Web Components，避免污染主题全局作用域。
 */

class ZenowellC9Capabilities extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    this.tabButtons = [...this.querySelectorAll('[data-zenowell-c9-tab]')];
    this.panels = [...this.querySelectorAll('[data-zenowell-c9-panel]')];
    this.panelMedia = [...this.querySelectorAll('[data-zenowell-c9-media]')];

    if (
      !this.tabButtons.length
      || this.tabButtons.length !== this.panels.length
      || this.tabButtons.length !== this.panelMedia.length
    ) return;

    this.classList.add('is-enhanced');
    for (const [index, button] of this.tabButtons.entries()) {
      button.addEventListener('click', () => this.#activateTab(index, false), {
        signal: this.#abortController.signal,
      });
      button.addEventListener('keydown', (event) => this.#handleKeyboard(event, index), {
        signal: this.#abortController.signal,
      });
    }

    this.addEventListener('shopify:block:select', (event) => this.#handleThemeEditorBlockSelect(event), {
      signal: this.#abortController.signal,
    });

    const selectedIndex = this.tabButtons.findIndex((button) => button.getAttribute('aria-selected') === 'true');
    this.#activateTab(selectedIndex >= 0 ? selectedIndex : 0, false);
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #activateTab(index, moveFocus) {
    if (!Number.isInteger(index) || index < 0 || index >= this.tabButtons.length) return;

    for (const [itemIndex, button] of this.tabButtons.entries()) {
      const isActive = itemIndex === index;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', String(isActive));
      button.tabIndex = isActive ? 0 : -1;
      this.panels[itemIndex].hidden = !isActive;
      this.panelMedia[itemIndex].hidden = !isActive;
    }

    if (moveFocus) this.tabButtons[index].focus();
  }

  /**
   * Shopify 主题编辑器选中能力 block 时，同步对应标签、文案与图片。
   * @param {CustomEvent} event Shopify block 选择事件。
   */
  #handleThemeEditorBlockSelect(event) {
    const selectedBlockId = event.detail?.blockId;
    const selectedButtonFromTarget = event.target.closest?.('[data-zenowell-c9-tab]');
    const selectedButton = selectedButtonFromTarget
      ?? this.tabButtons.find((button) => button.getAttribute('data-zenowell-c9-block-id') === selectedBlockId);
    const selectedIndex = this.tabButtons.indexOf(selectedButton);

    if (selectedIndex < 0) return;
    this.#activateTab(selectedIndex, false);
  }

  #handleKeyboard(event, currentIndex) {
    const lastIndex = this.tabButtons.length - 1;
    const targetByKey = {
      ArrowRight: currentIndex === lastIndex ? 0 : currentIndex + 1,
      ArrowLeft: currentIndex === 0 ? lastIndex : currentIndex - 1,
      Home: 0,
      End: lastIndex,
    };
    const targetIndex = targetByKey[event.key];

    if (targetIndex === undefined) return;
    event.preventDefault();
    this.#activateTab(targetIndex, true);
  }
}

class ZenowellC9Faq extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    this.items = [...this.querySelectorAll('details')];

    for (const item of this.items) {
      item.addEventListener('toggle', () => this.#closeSiblingItems(item), {
        signal: this.#abortController.signal,
      });
    }
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #closeSiblingItems(activeItem) {
    if (!activeItem.open) return;

    for (const item of this.items) {
      if (item !== activeItem) item.open = false;
    }
  }
}

class ZenowellC9Video extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    const videoTarget = this.querySelector('[data-zenowell-c9-video]');
    this.video = videoTarget?.querySelector?.('video') ?? videoTarget;
    this.playButton = this.querySelector('[data-zenowell-c9-video-play]');

    if (!this.video || !this.playButton) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const listenerOptions = { signal: this.#abortController.signal };

    this.playButton.addEventListener('click', () => this.#playVideo(), listenerOptions);
    this.video.addEventListener('play', () => this.#syncPlaybackState(), listenerOptions);
    this.video.addEventListener('pause', () => this.#syncPlaybackState(), listenerOptions);
    this.video.addEventListener('ended', () => this.#syncPlaybackState(), listenerOptions);
    this.#syncPlaybackState();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** 播放视频；浏览器拒绝播放时恢复按钮，继续允许用户重试。 */
  async #playVideo() {
    this.playButton.disabled = true;

    try {
      await this.video.play();
    } catch (error) {
      console.error('[ZenoWell C9] 视频播放失败。', error);
    } finally {
      this.playButton.disabled = false;
      this.#syncPlaybackState();
    }
  }

  /** 根据原生视频状态显示或隐藏中央播放按钮。 */
  #syncPlaybackState() {
    const isPlaying = !this.video.paused && !this.video.ended;
    this.classList.toggle('is-playing', isPlaying);
    this.playButton.hidden = isPlaying;
  }
}

class ZenowellC9Reserve extends HTMLElement {
  #abortController = new AbortController();
  #triggerElement = null;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    document.addEventListener('click', (event) => this.#handleDocumentClick(event), {
      signal: this.#abortController.signal,
    });
    this.dialog.addEventListener('click', (event) => this.#closeFromBackdrop(event), {
      signal: this.#abortController.signal,
    });
    this.dialog.addEventListener('close', () => this.#restoreFocus(), {
      signal: this.#abortController.signal,
    });

    if (this.querySelector('[data-zenowell-c9-form-state]')) this.#openDialog();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #handleDocumentClick(event) {
    const openButton = event.target.closest('[data-zenowell-c9-open-reserve]');
    const closeButton = event.target.closest('[data-zenowell-c9-close-reserve]');

    if (openButton) {
      event.preventDefault();
      this.#triggerElement = openButton;
      this.#openDialog();
      return;
    }

    if (closeButton && this.dialog.open) this.dialog.close();
  }

  #openDialog() {
    if (this.dialog.open) return;

    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
  }

  #closeFromBackdrop(event) {
    if (event.target === this.dialog) this.dialog.close();
  }

  #restoreFocus() {
    this.#triggerElement?.focus();
    this.#triggerElement = null;
  }
}

if (!customElements.get('zenowell-c9-capabilities')) {
  customElements.define('zenowell-c9-capabilities', ZenowellC9Capabilities);
}
if (!customElements.get('zenowell-c9-faq')) {
  customElements.define('zenowell-c9-faq', ZenowellC9Faq);
}
if (!customElements.get('zenowell-c9-video')) {
  customElements.define('zenowell-c9-video', ZenowellC9Video);
}
if (!customElements.get('zenowell-c9-reserve')) {
  customElements.define('zenowell-c9-reserve', ZenowellC9Reserve);
}
